import React, { useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useModal } from '../../../../../hooks/useModal'
import { MediaGalleryModal } from '../../../../../components/common/MediaGalleryModal'
import { Image } from '../../../../../components/common/Image'
import { infiniteTicketQueries } from '../../../../../api/ticket'
import { InfiniteScroll } from '../../../../../components/common/InfiniteScroll'
import { ErrorView } from '../../../../error/ErrorView/ErrorView'
import { IllustrationChoices } from '../../../../../components/common/Illustration/Illustration'
import { ITicket } from '../../../../../interfaces/models/ticket.interfaces'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'

interface TicketImagesProps {
  ticket?: ITicket
}

export const TicketImages = ({ ticket }: TicketImagesProps): JSX.Element => {
  const { translate } = useAppTranslation()
  const {
    data: query,
    fetchNextPage,
    isPending,
    isFetching,
  } = useInfiniteQuery(
    infiniteTicketQueries.getTicketMedia({
      ticketId: ticket?.id,
      societyId: ticket?.societyId,
    })
  )
  const media = query?.pages.flatMap((page) => page.items) || []
  const {
    show: showMediaGalleryModal,
    open: openMediaGalleryModal,
    close: closeMediaGalleryModal,
  } = useModal()
  const [mediaGalleryIndex, setMediaGalleryIndex] = useState<number>(0)
  const galleryMedia = media.map((_media) => ({
    id: _media._id,
    url: _media.url,
    createdAt: _media.createdAt,
  }))

  const openModal = (i: number): void => {
    setMediaGalleryIndex(i)
    openMediaGalleryModal()
  }

  const getMediaWidth = (): string => {
    switch (media?.length) {
      case 1:
        return 'flex-1/1'
      case 2:
        return 'flex-1/2'
      default:
        return 'flex-1/3'
    }
  }

  return (
    <div>
      <div
        className={`-mr-1 flex max-w-xl flex-col
        `}
      >
        <InfiniteScroll
          onEndReached={() => fetchNextPage({ cancelRefetch: false })}
          isFetching={isPending}
          key="TicketImages"
          className="flex w-full flex-wrap justify-end"
        >
          {!media.length && !isFetching && (
            <ErrorView
              title={translate('ticketSidebar.emptyImagesTitle')}
              illustration={IllustrationChoices.EMPTY}
            />
          )}
          {media?.map((_media, i) => (
            <div key={_media._id} className={getMediaWidth()}>
              <Image
                url={_media.url}
                imageClassName="rounded-2xl p-1 cursor-pointer"
                onClick={() => openModal(i)}
              />
            </div>
          ))}
          {showMediaGalleryModal && galleryMedia.length > 0 && (
            <MediaGalleryModal
              show={showMediaGalleryModal}
              close={closeMediaGalleryModal}
              media={galleryMedia}
              mediaGalleryIndex={mediaGalleryIndex}
              setMediaGalleryIndex={setMediaGalleryIndex}
            />
          )}
        </InfiniteScroll>
      </div>
    </div>
  )
}
